import { useTranslation } from 'react-i18next';
import { Icon, X } from '../../ui/Icon';
import { FilterMenu, type FilterDimension } from '../../ui/FilterMenu';

export interface ActiveFilter {
  /** Unique across dimensions — `${dimension}:${value}`. */
  id: string;
  /** "Dia", "Categoria"… or null for a filter that reads on its own ("Sense Pau"). */
  dimensionLabel: string | null;
  valueLabel: string;
  color?: string;
  onRemove: () => void;
}

/**
 * One line, whatever the number of filters. The menu holds the dimensions and
 * the bar shows only what is actually applied, so adding a filter later costs
 * an entry in `dimensions` and nothing in layout — the row of pills per
 * dimension it replaced could not say that.
 *
 * It sits above the cards on purpose: these filters drive the totals and the
 * charts too, not just the table underneath.
 */
export function LedgerFilterBar({ dimensions, active, visibleCount, onPick, onClear }: {
  dimensions: FilterDimension[];
  active: ActiveFilter[];
  visibleCount: number;
  onPick: (dimensionKey: string, value: string | null) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <FilterMenu dimensions={dimensions} onPick={onPick} />

      {active.map(f => (
        <span
          key={f.id}
          className="shrink-0 flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-mono text-[10px] font-semibold tracking-wide"
        >
          {f.color && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: f.color }} />}
          {f.dimensionLabel && <span className="opacity-60">{f.dimensionLabel}</span>}
          {f.valueLabel}
          <button
            type="button"
            onClick={f.onRemove}
            aria-label={t('expenses.filters.remove', { filter: f.valueLabel })}
            className="p-0.5 rounded hover:bg-white/20 dark:hover:bg-black/10 transition-colors cursor-pointer"
          >
            <Icon as={X} size={11} />
          </button>
        </span>
      ))}

      {active.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="shrink-0 px-2 py-1 font-mono text-[10px] font-semibold tracking-wide text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
        >
          {t('expenses.filters.clear')}
        </button>
      )}

      <span className="ml-auto shrink-0 font-mono text-[10px] tracking-wide text-slate-400 tabular-nums">
        {t('expenses.filters.showing', { count: visibleCount })}
      </span>
    </div>
  );
}
